import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertTriangle, TrendingUp, TrendingDown, X, Bell } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PopupAlertSystem = () => {
  const [alerts, setAlerts] = useState([]);
  const [currentAlert, setCurrentAlert] = useState(null);
  const [dismissed, setDismissed] = useState(new Set());

  const fetchAlerts = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const response = await axios.get(`${API}/stats/popup-alerts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Filter out dismissed alerts
      const activeAlerts = response.data.alerts.filter(
        alert => !dismissed.has(alert.id)
      );
      
      setAlerts(activeAlerts);
      
      // Show the first unread alert
      if (activeAlerts.length > 0 && !currentAlert) {
        setCurrentAlert(activeAlerts[0]);
      }
    } catch (error) {
      // Silent fail for polling
      console.log('Alert polling error:', error);
    }
  }, [dismissed, currentAlert]);

  useEffect(() => {
    // Initial fetch
    fetchAlerts();
    
    // Poll every 30 seconds
    const interval = setInterval(fetchAlerts, 30000);
    
    return () => clearInterval(interval);
  }, [fetchAlerts]);

  const handleDismiss = async (alertId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/stats/popup-alerts/${alertId}/dismiss`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setDismissed(prev => new Set([...prev, alertId]));
      setCurrentAlert(null);
      
      // Check for next alert
      const remaining = alerts.filter(a => a.id !== alertId && !dismissed.has(a.id));
      if (remaining.length > 0) {
        setTimeout(() => setCurrentAlert(remaining[0]), 500);
      }
    } catch (error) {
      toast.error('Failed to dismiss alert');
    }
  };

  const getAlertIcon = (type) => {
    switch (type) {
      case 'production_estimate':
        return <TrendingUp className="w-6 h-6 text-blue-500" />;
      case 'production_update':
        return <TrendingDown className="w-6 h-6 text-orange-500" />;
      case 'delay':
        return <AlertTriangle className="w-6 h-6 text-red-500" />;
      default:
        return <Bell className="w-6 h-6 text-gray-500" />;
    }
  };

  const getAlertColor = (type) => {
    switch (type) {
      case 'production_estimate':
        return 'bg-blue-50 border-blue-300';
      case 'production_update':
        return 'bg-orange-50 border-orange-300';
      case 'delay':
        return 'bg-red-50 border-red-300';
      default:
        return 'bg-gray-50 border-gray-300';
    }
  };

  if (!currentAlert) return null;

  return (
    <Dialog open={!!currentAlert} onOpenChange={() => handleDismiss(currentAlert.id)}>
      <DialogContent 
        className={`sm:max-w-[450px] ${getAlertColor(currentAlert.alert_type)} border-2`}
        data-testid="popup-alert-modal"
      >
        <DialogHeader>
          <div className="flex items-center gap-3">
            {getAlertIcon(currentAlert.alert_type)}
            <DialogTitle className="text-lg">
              {currentAlert.alert_type === 'production_estimate' && 'Production Estimate Update'}
              {currentAlert.alert_type === 'production_update' && 'Production Changed'}
              {currentAlert.alert_type === 'delay' && 'Task Delay Reported'}
              {!['production_estimate', 'production_update', 'delay'].includes(currentAlert.alert_type) && 'Alert'}
            </DialogTitle>
          </div>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {/* Alert Content */}
          <div className="p-4 bg-white rounded-lg border">
            <p className="text-gray-900 font-medium">{currentAlert.message}</p>
            
            {currentAlert.room_name && (
              <div className="mt-3 flex items-center gap-2">
                <Badge variant="outline">{currentAlert.room_name}</Badge>
                {currentAlert.batch_name && (
                  <span className="text-sm text-gray-500">{currentAlert.batch_name}</span>
                )}
              </div>
            )}

            {currentAlert.data && (
              <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                {currentAlert.data.morning_estimate && (
                  <div className="p-2 bg-yellow-50 rounded">
                    <span className="text-gray-500">Morning:</span>
                    <span className="ml-2 font-bold">{currentAlert.data.morning_estimate} kg</span>
                  </div>
                )}
                {currentAlert.data.afternoon_update && (
                  <div className="p-2 bg-blue-50 rounded">
                    <span className="text-gray-500">Afternoon:</span>
                    <span className="ml-2 font-bold">{currentAlert.data.afternoon_update} kg</span>
                  </div>
                )}
                {currentAlert.data.change_percent && (
                  <div className={`p-2 rounded col-span-2 ${
                    currentAlert.data.change_percent > 0 ? 'bg-green-50' : 'bg-red-50'
                  }`}>
                    <span className="text-gray-500">Change:</span>
                    <span className={`ml-2 font-bold ${
                      currentAlert.data.change_percent > 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {currentAlert.data.change_percent > 0 ? '+' : ''}{currentAlert.data.change_percent}%
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Timestamp */}
          <p className="text-xs text-gray-500 text-center">
            {format(new Date(currentAlert.created_at), 'PPp')}
          </p>

          {/* Remaining alerts indicator */}
          {alerts.length > 1 && (
            <p className="text-xs text-center text-gray-500">
              {alerts.length - 1} more alert{alerts.length - 1 > 1 ? 's' : ''} pending
            </p>
          )}

          {/* Actions */}
          <div className="flex gap-3">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => handleDismiss(currentAlert.id)}
              data-testid="dismiss-alert-btn"
            >
              <X className="w-4 h-4 mr-2" />
              Dismiss
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PopupAlertSystem;
